import React from 'react'
import { Dialog } from '@mui/material'
import { Close as CloseIcon, ContentCopy as CopyIcon, EditOutlined } from '@mui/icons-material'
import { Package } from '../../store/package/reducer'
import iconProfilerLookup from '../../asset/sprite/quota_lookups.png'
import iconProfilerCrawlers from '../../asset/sprite/quota_sn.png'
import iconProfilerCases from '../../asset/sprite/quota_cases.png'
import iconWebint from '../../asset/sprite/quota_webint.png'
import iconWebintMonitoring from '../../asset/sprite/quota_monitoring.png'
import iconWebintCases from '../../asset/sprite/quota_webint_cases.png'
import { AuditLog } from './AuditLog'
import l from '../../page/SubscribersPageV2.module.css'
import s from './PackagePreviewDialog.module.css'

/*
 * "How the user sees it" — a package drawn as the user's Packages page draws
 * it (espy-dashboard component/PackageItem.tsx + sass/plan-card.sass, the
 * new plan card), with the same Monthly / Annual switch. Ported, not shared:
 * the two apps do not share code. Purely a preview — the buy / demo buttons
 * do nothing here.
 *
 * Irbis Pro packages (webinthub, its sub-package and upgrade) get the same
 * card with the Irbis PRO / Advanced badge and the six quota counters the
 * user's Pro card shows.
 */

const CURRENCY = '€'

const ICON: Record<string, string> = { one_time_fee: '●', personal: '◎', general: '✦', custom: '◆' }

function duration(p: Package, annual: boolean): string {
  if (p.type === ('one_time_fee' as any)) return 'One Time Fee'
  if (p.price && annual && p.annualPrice && p.annualProductId) return 'per month'
  const days = Number((p as any).activeDay)
  if ([28, 29, 30, 31].includes(days)) return 'per month'
  return `per ${Number.isFinite(days) ? days : '—'} days`
}

/* the user's checkPrice: the annual price shown per month */
const priceOf = (p: Package, annual: boolean) =>
  (p.price && annual && p.annualPrice && p.annualProductId ? (p.annualPrice / 12).toFixed(0) : p.price)

/* the user's checkCredits: general and personal packages only */
function credits(p: Package, annual: boolean): number | null {
  if (p.type !== 'general' && p.type !== 'personal') return null
  if (!p.creditsPerUnit || p.price == null) return null
  if (annual && p.annualPrice && p.annualProductId) return Math.round(p.price * p.creditsPerUnit * 12)
  return p.price * p.creditsPerUnit
}

const QUOTAS: [string, string, string][] = [
  [iconProfilerLookup, 'quotaProfilerLookup', 'Profiler lookups'],
  [iconProfilerCrawlers, 'quotaProfilerCrawlers', 'Profiler crawlers'],
  [iconProfilerCases, 'quotaProfilerCases', 'Profiler cases'],
  [iconWebint, 'quotaWebIntCrawlers', 'WebInt crawlers'],
  [iconWebintMonitoring, 'quotaWebIntMonitoring', 'WebInt monitoring'],
  [iconWebintCases, 'quotaWebIntCases', 'WebInt cases'],
]

/** the user's plan card — also the live preview in PopupPackageV2 */
export function PlanCard({ p, annual }: { p: Package; annual: boolean }) {
  const isCustom = p.type === 'custom'
  const pro = p.type === 'webinthub' || p.type === 'webinthub_subpackage' ? 'Irbis PRO'
    : p.type === 'webinthub_upgrade' ? 'Irbis Advanced' : null
  const features = [p.feature1, p.feature2, p.feature3].flatMap((f) => (f?.title ? [f.title] : []))
  const cr = credits(p, annual)
  const quotas = (p as any).packageQuotas || {}

  return (
    <article className={`${s.card} ${isCustom ? s.ribbonCard : ''} ${p.isBestProposition ? s.best : ''}`}>
      {isCustom && <span className={s.ribbon}>Enterprise</span>}
      {p.isBestProposition && <span className={s.bestPill}>Best offer</span>}
      {pro && <span className={s.proBadge}>{pro}</span>}

      <div className={s.header}>
        <div className={s.title}>
          <span className={s.titleIcon}>{ICON[p.type] || '●'}</span>
          <h2 title={p.title}>{p.title}</h2>
        </div>
        {p.subTitle?.context && <div className={s.subtitle}>{p.subTitle.context}</div>}
        {isCustom ? (
          <button type="button" className={`${s.secondary} ${s.demo}`}>Book a Demo</button>
        ) : (
          <>
            <div className={s.price}>
              <strong>{CURRENCY}{priceOf(p, annual)}</strong>
              <span>
                {annual && p.annualPrice ? <s>{CURRENCY}{priceOf(p, false)}</s> : null}
                {' '}{duration(p, annual)}
              </span>
            </div>
            {p.type !== ('one_time_fee' as any) && <span className={s.muted}>{annual ? 'billed annual' : 'billed monthly'}</span>}
          </>
        )}
        <p className={s.credit}>{cr != null ? <><b>{cr}</b> credits</> : ' '}</p>
      </div>

      <hr />

      {pro && (
        <div className={s.quotas}>
          {QUOTAS.map(([icon, key, label]) => {
            const v = Number(quotas[key])
            return (
              <div key={key} className={s.quota} title={label}>
                <img src={icon} alt="" />
                <span>{Number.isFinite(v) ? (annual ? v * 12 : v) : '—'}</span>
              </div>
            )
          })}
        </div>
      )}

      <ul>
        {features.map((f, i) => <li key={i}>{f}</li>)}
      </ul>

      {p.isFree ? (
        <span className={s.badge}>Active</span>
      ) : (
        <button type="button" className={s.secondary}>{p.button || 'Buy'}</button>
      )}
    </article>
  )
}

export function PackagePreviewDialog({ pkg, theme, onClose, onEdit, onDuplicate }: {
  pkg: Package
  theme: 'light' | 'dark'
  onClose: () => void
  onEdit: () => void
  /** undefined hides the button (custom / demo packages cannot be copied) */
  onDuplicate?: () => void
}) {
  const hasAnnual = !!(pkg.annualPrice && pkg.annualProductId)
  // the user's page opens on Annual
  const [annual, setAnnual] = React.useState(true)
  // the card, or what admins changed in this package (admin_audit)
  const [view, setView] = React.useState<'preview' | 'history'>('preview')
  const saving = pkg.price && pkg.annualPrice ? Math.round(((pkg.price * 12 - pkg.annualPrice) / (pkg.price * 12)) * 100) : 0

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, overflow: 'visible' } }}
    >
      <div className={`${l.theme} ${s.shell}`} data-theme={theme}>
        <div className={s.top}>
          <div>
            <small className={s.eyebrow}>Preview</small>
            <b className={s.heading}>How the user sees this package</b>
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close"><CloseIcon sx={{ fontSize: 18 }} /></button>
        </div>

        <div className={s.viewTabs}>
          <button type="button" className={`${s.viewTab} ${view === 'preview' ? s.viewOn : ''}`} onClick={() => setView('preview')}>Preview</button>
          <button type="button" className={`${s.viewTab} ${view === 'history' ? s.viewOn : ''}`} onClick={() => setView('history')}>History</button>
        </div>

        {view === 'history' ? (
          <div className={s.history}><AuditLog entityType="package" entityId={pkg.id} compact /></div>
        ) : (<>
        <div className={s.toggle}>
          <button type="button" className={`${s.opt} ${!annual ? s.optOn : ''}`} onClick={() => setAnnual(false)}>Monthly</button>
          <button type="button" className={`${s.opt} ${annual ? s.optOn : ''}`} onClick={() => setAnnual(true)}>
            Annual{saving > 0 && hasAnnual ? <span className={s.save}>-{saving}%</span> : null}
          </button>
        </div>
        {annual && !hasAnnual && pkg.type !== 'custom' && (
          <div className={s.note}>No annual product — the user sees the monthly price here too.</div>
        )}

        <div className={s.stage}>
          <PlanCard p={pkg} annual={annual} />
        </div>
        </>)}

        <div className={s.foot}>
          <span className={s.footNote}>{pkg.isActive ? 'Visible to users' : 'Inactive — not shown to users'}</span>
          {onDuplicate && (
            <button type="button" className={s.dup} onClick={onDuplicate}><CopyIcon sx={{ fontSize: 16 }} />Duplicate</button>
          )}
          <button type="button" className={`${s.edit} ${onDuplicate ? s.editNext : ''}`} onClick={onEdit}><EditOutlined sx={{ fontSize: 17 }} />Edit package</button>
        </div>
      </div>
    </Dialog>
  )
}
